import { JobFit } from '@components/job-card/job-card-vo';
import { credentialGap } from './utils/credential-tier.util';

/**
 * Determines interest-match fit based on the user's per-trait RIASEC signal
 * and the occupation's top 2 RIASEC trait IDs.
 *
 * Signal classification (trait score = average of 5 answers, SMILE=7 MEH=4 FROWN=1):
 *   Positive: score >= 5.2  (requires at least 2 SMILEs -- 2 SMILE + 3 MEH = 5.2)
 *   Neutral:  4.0 <= score < 5.2  (all MEH baseline, or 1 SMILE + 4 MEH = 4.6)
 *   Negative: score < 4.0
 *
 * Fit rules:
 *   great -- positive signal in BOTH top traits
 *   good  -- positive signal in ONE of the two top traits
 *   ok    -- neutral signal in at least one top trait, no positive in either
 *   null  -- negative signal in both top traits (or no trait data)
 *
 * The positive threshold was raised from 4.0 to 5.2 so a single SMILE on a trait
 * is no longer enough to label it as enthusiastic. The neutral band [4.0, 5.2)
 * preserves visibility for users with mild interest (one SMILE on a top trait)
 * by demoting them from GOOD to OK rather than dropping them off the results page.
 *
 * EPSILON guards the >= 5.2 comparison against IEEE 754 representation of 5.2,
 * which is not exact (closest double is ~5.2000000000000002). Same for 4.0.
 *
 * Always matches on interestId integer (1-6), never on trait name strings.
 * The gateway uses "Enterprise"; PathBuilder uses "Enterprising" -- numeric
 * IDs are consistent across both systems.
 */
export const jobFitByTraitSignal = (userTraitScores: Record<number, number>, topTwoTraitIds: number[]): JobFit => {
  if (!topTwoTraitIds?.length || !Object.keys(userTraitScores ?? {}).length) {
    return null;
  }

  const POSITIVE_THRESHOLD = 5.2;
  const NEUTRAL_FLOOR = 4.0;
  const EPSILON = 1e-9;

  const signals = topTwoTraitIds.map(id => {
    const score = userTraitScores[id] ?? 0;
    if (score >= POSITIVE_THRESHOLD - EPSILON) return 'positive';
    if (score >= NEUTRAL_FLOOR - EPSILON) return 'neutral';
    return 'negative';
  });

  const positiveCount = signals.filter(s => s === 'positive').length;
  const hasNeutral = signals.some(s => s === 'neutral');

  if (positiveCount === topTwoTraitIds.length) {
    return 'great';
  }
  if (positiveCount > 0) {
    return 'good';
  }
  if (hasNeutral) {
    return 'ok';
  }
  return null;
};

const jobFitByOnetCode = (onetCode: string, jobs: any[]): JobFit => {
  for (const option of jobs) {
    if (option.greatFit.some(item => item.onetCode === onetCode)) {
      return 'great';
    }
    if (option.goodFit.some(item => item.onetCode === onetCode)) {
      return 'good';
    }
    if (option.okFit.some(item => item.onetCode === onetCode)) {
      return 'ok';
    }
  }
  return null;
};

export type SkillSignal = 'strong' | 'moderate' | 'weak' | 'none';

/**
 * Returns the strongest skill-signal level for a given job's onet code across
 * all skill-aggregation sources. Each source has greatFit / goodFit / okFit
 * arrays of { onetCode } objects.
 *
 * Priority: strong > moderate > weak > none.
 *
 * Unlike jobFitByOnetCode (which returns the first source's tier), this
 * function scans every source and returns the BEST signal. That matters when
 * a job appears in multiple sources at different tiers — the strongest source
 * is the one that drives the user-facing fit, not whichever source happens to
 * be first in the aggregation order.
 */
export const skillSignalStrength = (onetCode: string, sources: any[] | null | undefined): SkillSignal => {
  if (!sources?.length) {
    return 'none';
  }

  let best: SkillSignal = 'none';
  for (const source of sources) {
    if (source?.greatFit?.some?.((item: any) => item.onetCode === onetCode)) {
      return 'strong'; // best possible -- short-circuit
    }
    if (best !== 'moderate' && source?.goodFit?.some?.((item: any) => item.onetCode === onetCode)) {
      best = 'moderate';
    } else if (best === 'none' && source?.okFit?.some?.((item: any) => item.onetCode === onetCode)) {
      best = 'weak';
    }
  }
  return best;
};

/**
 * Applies the credential-aligned tier-resolution matrix, with TWO signal
 * inputs: one for actual work/military experience, one for program-of-study
 * CIP alignment.
 *
 *   Gap        | exp=strong OR cip=strong | (other) moderate/weak | both none/weak
 *   -----------+--------------------------+-----------------------+----------
 *   exact      | great if exp-strong;     | good (floor)          | good (floor)
 *              |   else good              |                       |
 *   over 1     | ok (cap)                 | hidden                | hidden
 *   over 2+    | ok (cap)                 | hidden                | hidden
 *   under 1    | ok                       | ok                    | ok (NEW floor: next-step suggestion)
 *   under 2    | ok if exp-strong only    | hidden                | hidden
 *   under 3+   | hidden                   | hidden                | hidden
 *
 * Visibility rules:
 *   - Over-1 / Over-2+ / Under-2: require STRONG signal to surface (else hidden)
 *   - Under-1: ALWAYS visible as OK (asymmetric "next-step floor"). The role
 *     is one credential tier above the user; we treat it as a career-growth
 *     suggestion even with no skill signal.
 *
 * Special case: userTier === 0 (LESS_THAN_HIGH_SCHOOL) → always null. The
 * banking corpus has no below-HS roles, so a tier-0 user can never be at-tier
 * for any job and the matrix would never produce a non-null result anyway.
 *
 * Caller MUST supply both tiers as 1-5 (or 0 for below-HS). If either is null
 * the caller should fall back to skill-only matching, not call this function.
 *
 * Design rationale:
 *   - GREAT requires actual experience signal -- "you've done relevant work
 *     for this role" -- not just a related degree. CIP greatFit alone earns
 *     GOOD at exact credential, never GREAT.
 *   - GOOD floor for exact credential: a user whose level matches the role's
 *     requirement sees the role as at least GOOD even with no signal at all.
 *   - OK floor for under-1: a user one tier below sees the role as OK
 *     ("you could grow into this") even with no other signal. Asymmetric
 *     with over-1 because surfacing a job below the user's level with no
 *     signal is not informative, while surfacing the next step up is.
 *   - Over (any amount) and Under-2 require STRONG signal to surface.
 *   - Under-2 specifically requires EXPERIENCE-strong; CIP alone does not
 *     bridge a 2-tier credential gap.
 *   - Under-3+ is never reachable regardless of any signal.
 */
export const applyCredentialMatrix = (
  experienceSignal: SkillSignal,
  cipSignal: SkillSignal,
  userTier: number,
  roleTier: number
): JobFit => {
  if (userTier === 0) {
    return null;
  }

  const gap = credentialGap(userTier, roleTier);
  const hasStrongSignal = experienceSignal === 'strong' || cipSignal === 'strong';

  if (gap.direction === 'exact') {
    // GREAT requires experience-strong. CIP-strong alone earns GOOD (floor).
    if (experienceSignal === 'strong') return 'great';
    return 'good';
  }

  if (gap.direction === 'over') {
    // Overqualified by any amount: cap at OK only when there's STRONG signal
    // from any source. Moderate/weak/none hides.
    if (!hasStrongSignal) return null;
    return 'ok';
  }

  // direction === 'under'
  if (gap.delta === 1) {
    // Under-1: ALWAYS visible as OK (asymmetric next-step floor). The role
    // is one credential tier above the user; treat as career-growth even
    // without skill signal. Symmetric with the exact-credential GOOD floor.
    return 'ok';
  }
  if (gap.delta === 2) {
    // Under-2 specifically requires EXPERIENCE-strong to compensate.
    // CIP-strong alone does not bridge a 2-tier credential gap.
    if (experienceSignal === 'strong') return 'ok';
    return null;
  }
  // under by 3 or more: never reachable, hide regardless of signal
  return null;
};

const bestJobFit = (...options: JobFit[]): JobFit => {
  if (options.includes('great')) {
    return 'great';
  }
  if (options.includes('good')) {
    return 'good';
  }
  if (options.includes('ok')) {
    return 'ok';
  }
  return null;
};

/**
 * Routes a job through the appropriate fit calculation based on the active filter.
 *
 * Skill signals are now TWO axes that the credential-aware path keeps
 * separate:
 *
 *   experienceSources (4th positional, formerly named `jobs`): work-experience
 *     job-family + previousJobs + military (military O*NET codes feed into the
 *     same gateway job-family query, so they land in this source bucket).
 *
 *   cipSources (9th positional, new): degreeProgramOccupations from the
 *     user's program-of-study CIP code(s).
 *
 * For `skills-match` and `best-match` filters, the credential-aligned matrix
 * applies when both `userCredentialTier` and `roleCredentialTier` are
 * supplied. When either is null (legacy callers like my-fit.component.ts),
 * falls back to skill-only matching via jobFitByOnetCode against the
 * COMBINED experience+CIP source pool, preserving today's behavior for those
 * callers.
 *
 * For `interest-match`, both source arrays are ignored; that filter uses
 * RIASEC trait signal only.
 */
export const jobFit = (
  filter: string,
  onetCode: string,
  interestScore: number,
  experienceSources: any[],
  userTraitScores: Record<number, number> = {},
  topTwoTraitIds: number[] = [],
  userCredentialTier: number | null = null,
  roleCredentialTier: number | null = null,
  cipSources: any[] = []
): JobFit => {
  if (filter === 'interest-match') {
    return jobFitByTraitSignal(userTraitScores, topTwoTraitIds);
  }

  const credentialAware =
    userCredentialTier !== null &&
    userCredentialTier !== undefined &&
    roleCredentialTier !== null &&
    roleCredentialTier !== undefined;

  let skillsFit: JobFit;
  if (credentialAware) {
    const experienceSignal = skillSignalStrength(onetCode, experienceSources);
    const cipSignal = skillSignalStrength(onetCode, cipSources);
    skillsFit = applyCredentialMatrix(experienceSignal, cipSignal, userCredentialTier!, roleCredentialTier!);
  } else {
    // Legacy fallback: combine experience and CIP into a single pool and use
    // the first-source-wins lookup. Preserves today's behavior for callers
    // (like my-fit.component.ts) that don't yet pass credential tiers.
    skillsFit = jobFitByOnetCode(onetCode, [...experienceSources, ...cipSources]);
  }

  if (filter === 'skills-match') {
    return skillsFit;
  }

  // best-match: take the better of trait-signal-based interest fit and the
  // (possibly credential-aware) skills fit.
  return bestJobFit(jobFitByTraitSignal(userTraitScores, topTwoTraitIds), skillsFit);
};
